import { SpecificChartFunctionality } from 'finviz-charts/app/constants/common'
import * as React from 'react'

import { TIMEFRAME } from '../../main/constants'
import { useWindowSize } from '../../main/hooks/use-window-size'
import { getIntradayChartConfig, useChartLayoutGlobalModelAsync, useModelState } from '../charts-lib'

export function getChartLayoutSizeConfig(
  layoutConfig: Pick<
    typeof window.globalChartConfig,
    'idea' | 'barWidth' | 'barMargin' | 'charts' | 'specificChartFunctionality'
  >,
  p = TIMEFRAME.d
) {
  const { clientWidth } = document.body
  const { premarket, aftermarket, dateRange } = layoutConfig.charts[0]
  const maxChartWidth = clientWidth - 40
  let width = 990
  let barWidth = 3
  let barMargin = 1
  let timeframeMinutes = 0
  const appliedPeriod =
    layoutConfig.specificChartFunctionality === SpecificChartFunctionality.quotePage ? p : TIMEFRAME.d
  switch (appliedPeriod) {
    case TIMEFRAME.i1:
      timeframeMinutes = 1
      barWidth = 1
      if (premarket || aftermarket || clientWidth < 1650) {
        barMargin = 0
      }
      break
    case TIMEFRAME.i2:
      timeframeMinutes = 2
      break
    case TIMEFRAME.i3:
      timeframeMinutes = 3
      if (premarket && aftermarket && clientWidth <= 1505) {
        barWidth = 2
      }
      break
    case TIMEFRAME.i5:
      timeframeMinutes = 5
      barWidth = 5
      barMargin = clientWidth >= 1100 ? 2 : 1
      break
    case TIMEFRAME.i10:
      width = 1058 // 39bars per day * 5days * 5px per bar + 35px left offset + 48px right offset
      barMargin = 0
      break
    case TIMEFRAME.i15:
    case TIMEFRAME.i30:
      width = 1019
      break
    case TIMEFRAME.h:
      width = 1008
      break
    case TIMEFRAME.h2:
    case TIMEFRAME.h4:
      width = 1020
      break
    case TIMEFRAME.d:
      width = 1282
      break
    case TIMEFRAME.w:
    case TIMEFRAME.m:
      width = 994
      break
  }

  switch (p) {
    case TIMEFRAME.i1:
    case TIMEFRAME.i2:
    case TIMEFRAME.i3:
    case TIMEFRAME.i5:
      const commonProps = {
        timeframeMinutes,
        barWidthWithMargin: barWidth + 2 + barMargin,
        hasAftermarket: aftermarket,
        hasPremarket: premarket,
      }
      width = getIntradayChartConfig({
        ...commonProps,
        maxWidth: maxChartWidth,
        canCropChart: !dateRange,
      }).chartWidthWithOffset
      break
  }

  // 9px * 2 for margin left/right + 1px * 2 for border
  if (maxChartWidth <= width) {
    width = Math.max(994, maxChartWidth)
    if (p === TIMEFRAME.d) {
      barWidth = 2
      barMargin = 0
      width = 991
    }
  }

  if (layoutConfig.idea) {
    barWidth = layoutConfig.barWidth
    barMargin = layoutConfig.barMargin
  }

  return {
    width,
    barWidth,
    barMargin,
  }
}

export function useChartLayoutWidthWatch() {
  const { innerWidth } = useWindowSize()
  const { chartLayoutModel } = useChartLayoutGlobalModelAsync()
  const unwatchedFirstChart = React.useMemo(() => chartLayoutModel?.charts().all()[0] ?? null, [chartLayoutModel])
  const chartModel = useModelState(unwatchedFirstChart, { watchProperties: ['quote'] })
  const quote = useModelState(chartModel?.quote() ?? null, { watchProperties: ['ticker', 'timeframe', 'isFetching'] })
  const ideaId = chartLayoutModel?.idea?.id

  React.useEffect(() => {
    const settings = !ideaId ? chartLayoutModel?.settings : null
    const timeframe = quote?.timeframe
    if (chartLayoutModel && timeframe && settings) {
      const { width, barWidth, barMargin } = getChartLayoutSizeConfig(chartLayoutModel.toConfig(), timeframe)
      const newSettings = JSON.parse(JSON.stringify(settings))
      newSettings.ChartSettings.center.barWidth = barWidth
      newSettings.IndicatorSettings.center.barWidth = barWidth
      newSettings.ChartSettings.center.barMargin = barMargin
      newSettings.IndicatorSettings.center.barMargin = barMargin
      chartLayoutModel.updateAttributes({ width, settings: newSettings })
    }
    // We don't want to trigger this effect on any chartLayoutModel change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.premarket, quote?.aftermarket, quote?.timeframe, ideaId, innerWidth])
}
